export const operationalStatusOptions = [
  { value: 'operational', label: 'Operational' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' },
  { value: 'offline', label: 'Offline' },
];

export const lifecycleStatusOptions = [
  { value: 'operational', label: 'Operational' },
  { value: 'cod_achieved', label: 'Commercial Operation Date (COD) Achieved' },
  { value: 'performance_testing', label: 'Performance Testing' },
  { value: 'grid_connection_in_progress', label: 'Grid Connection in Progress' },
  { value: 'under_construction', label: 'Under Construction' },
  { value: 'under_development', label: 'Under Development' },
  { value: 'planned_initiative', label: 'Planned Initiative' },
];

export const assetTechnologyOptions = [
  { value: 'solar', label: 'Solar' },
  { value: 'battery', label: 'BESS' },
  { value: 'wind', label: 'Wind' },
];

export const assetTypeOptionsByTechnology = {
  solar: [
    { value: 'ground_mount', label: 'Ground mount' },
    { value: 'rooftop', label: 'Rooftop' },
    { value: 'floating', label: 'Floating' },
  ],
  battery: [
    { value: 'standalone', label: 'Stand-alone' },
    { value: 'hybrid_solar', label: 'Hybrid - Solar' },
    { value: 'hybrid_wind', label: 'Hybrid - Wind' },
  ],
  wind: [
    { value: 'onshore', label: 'Onshore' },
    { value: 'offshore', label: 'Offshore' },
  ],
};

export const companySections = [
  { key: 'spv', label: 'SPV', companyType: 'spv' },
  { key: 'holding_company', label: 'Holding Company', companyType: 'holding_company' },
  { key: 'om_company', label: 'O&M Company', companyType: 'om_company' },
  { key: 'epc_company', label: 'EPC Company', companyType: 'epc_company' },
  { key: 'lender', label: 'Lender', companyType: 'lender' },
];

export const companyTypeOptions = companySections.map((section) => ({
  value: section.companyType,
  label: section.label,
}));

export const keyDateFields = [
  { key: 'grid_connection_date', label: 'Grid connection date' },
  { key: 'commissioning_date', label: 'Commissioning' },
  { key: 'commercial_operation_date', label: 'Commercial operation' },
  { key: 'management_start_date', label: 'Management start date' },
  { key: 'management_end_date', label: 'Management end date' },
  { key: 'contract_renewal_date', label: 'Contract renewal date' },
  { key: 'sold_date', label: 'Sold date' },
];

const baseAssetForm = {
  asset_id: '',
  name: '',
  type: 'solar',
  asset_type: 'ground_mount',
  portfolio_id: '',
  portfolio: '',
  sub_portfolio_id: '',
  sub_portfolio: '',
  lifecycle_status: 'planned_initiative',
  operational_status: 'operational',
  dc_installed_capacity_kwp: '',
  export_capacity_kva: '',
  import_capacity_kva: '',
  asset_manager_id: '',
  asset_manager_name: '',
  spv_company_id: '',
  spv_name: '',
  spv_contact_person: '',
  spv_contact_email: '',
  spv_contact_phone: '',
  holding_company_id: '',
  holding_company_name: '',
  holding_company_contact_person: '',
  holding_company_contact_email: '',
  holding_company_contact_phone: '',
  om_company_id: '',
  om_company_name: '',
  om_company_contact_person: '',
  om_company_contact_email: '',
  om_company_contact_phone: '',
  epc_company_id: '',
  epc_company_name: '',
  epc_company_contact_person: '',
  epc_company_contact_email: '',
  epc_company_contact_phone: '',
  lender_company_id: '',
  lender_name: '',
  lender_contact_person: '',
  lender_contact_email: '',
  lender_contact_phone: '',
  address_line: '',
  city: '',
  state_region: '',
  country: '',
  pincode: '',
  grid_connection_date: '',
  commissioning_date: '',
  commercial_operation_date: '',
  management_start_date: '',
  management_end_date: '',
  contract_renewal_date: '',
  sold_date: '',
  latitude: '',
  longitude: '',
};

export function getAssetTypeOptions(technology) {
  return assetTypeOptionsByTechnology[technology] || assetTypeOptionsByTechnology.solar;
}

function getDefaultAssetType(technology) {
  return getAssetTypeOptions(technology)[0]?.value || '';
}

export function createEmptyAssetForm() {
  return { ...baseAssetForm };
}

export function normalizeAsset(asset = {}) {
  const type = asset.type || 'solar';
  const assetType = asset.asset_type || getDefaultAssetType(type);
  const portfolio = asset.portfolio || asset.group || '';
  const subPortfolio = asset.sub_portfolio || asset.site || '';
  const operationalStatus = asset.operational_status || asset.status || 'operational';

  return {
    ...asset,
    type,
    asset_type: assetType,
    portfolio_id: asset.portfolio_id || '',
    portfolio,
    sub_portfolio_id: asset.sub_portfolio_id || '',
    sub_portfolio: subPortfolio,
    group: asset.group ?? portfolio,
    site: asset.site ?? subPortfolio,
    lifecycle_status: asset.lifecycle_status || 'planned_initiative',
    operational_status: operationalStatus,
    status: asset.status || operationalStatus,
    dc_installed_capacity_kwp: asset.dc_installed_capacity_kwp ?? 0,
    export_capacity_kva: asset.export_capacity_kva ?? '',
    import_capacity_kva: asset.import_capacity_kva ?? '',
    asset_manager_id: asset.asset_manager_id || '',
    asset_manager_name: asset.asset_manager_name || '',
    spv_company_id: asset.spv_company_id || '',
    holding_company_id: asset.holding_company_id || '',
    om_company_id: asset.om_company_id || '',
    epc_company_id: asset.epc_company_id || '',
    lender_company_id: asset.lender_company_id || '',
    address_line: asset.address_line || '',
    city: asset.city || '',
    state_region: asset.state_region || '',
    country: asset.country || '',
    pincode: asset.pincode || '',
  };
}

export function mapAssetToForm(asset = {}) {
  const normalized = normalizeAsset(asset);
  const form = createEmptyAssetForm();

  return {
    ...form,
    ...normalized,
    dc_installed_capacity_kwp:
      normalized.dc_installed_capacity_kwp === 0 ? '' : String(normalized.dc_installed_capacity_kwp),
    export_capacity_kva:
      normalized.export_capacity_kva === '' || normalized.export_capacity_kva === null
        ? ''
        : String(normalized.export_capacity_kva),
    import_capacity_kva:
      normalized.import_capacity_kva === '' || normalized.import_capacity_kva === null
        ? ''
        : String(normalized.import_capacity_kva),
    latitude:
      normalized.location && normalized.location.lat !== undefined ? String(normalized.location.lat) : '',
    longitude:
      normalized.location && normalized.location.lon !== undefined ? String(normalized.location.lon) : '',
  };
}

export function attachPortfolioLinks(asset = {}, portfolios = [], subPortfolios = []) {
  const normalized = normalizeAsset(asset);
  const scopedPortfolios = portfolios.filter(
    (record) => !normalized.org_id || !record.org_id || record.org_id === normalized.org_id
  );
  const scopedSubPortfolios = subPortfolios.filter(
    (record) => !normalized.org_id || !record.org_id || record.org_id === normalized.org_id
  );

  const normalizeName = (value) => (value || '').trim().toLowerCase();
  const matchPortfolioByName = (name) =>
    scopedPortfolios.find((record) => normalizeName(record.name) === normalizeName(name));
  const matchSubPortfolioByName = (name) =>
    scopedSubPortfolios.find((record) => normalizeName(record.name) === normalizeName(name));

  let portfolioRecord =
    (normalized.portfolio_id && scopedPortfolios.find((record) => record.id === normalized.portfolio_id)) || null;
  let subPortfolioRecord =
    (normalized.sub_portfolio_id &&
      scopedSubPortfolios.find((record) => record.id === normalized.sub_portfolio_id)) ||
    null;

  if (!portfolioRecord && normalized.portfolio) {
    portfolioRecord = matchPortfolioByName(normalized.portfolio) || null;
  }
  if (!subPortfolioRecord && normalized.sub_portfolio) {
    subPortfolioRecord = matchSubPortfolioByName(normalized.sub_portfolio) || null;
  }

  // Legacy compatibility: older assets may have group/site values swapped relative to portfolio/sub-portfolio.
  if (!subPortfolioRecord && normalized.portfolio) {
    const swappedSubPortfolio = matchSubPortfolioByName(normalized.portfolio);
    const swappedPortfolio = normalized.sub_portfolio
      ? matchPortfolioByName(normalized.sub_portfolio)
      : null;
    const compatiblePair =
      swappedSubPortfolio && (!swappedPortfolio || swappedSubPortfolio.portfolio_id === swappedPortfolio.id);

    if (compatiblePair) {
      subPortfolioRecord = swappedSubPortfolio;
      portfolioRecord =
        swappedPortfolio ||
        portfolioRecord ||
        scopedPortfolios.find((record) => record.id === swappedSubPortfolio.portfolio_id) ||
        null;
    }
  }

  if (subPortfolioRecord && !portfolioRecord && subPortfolioRecord.portfolio_id) {
    portfolioRecord =
      scopedPortfolios.find((record) => record.id === subPortfolioRecord.portfolio_id) || null;
  }

  if (
    subPortfolioRecord &&
    portfolioRecord &&
    subPortfolioRecord.portfolio_id &&
    subPortfolioRecord.portfolio_id !== portfolioRecord.id
  ) {
    portfolioRecord =
      scopedPortfolios.find((record) => record.id === subPortfolioRecord.portfolio_id) || portfolioRecord;
  }

  const nextPortfolio = portfolioRecord?.name || normalized.portfolio || '';
  const nextSubPortfolio = subPortfolioRecord?.name || normalized.sub_portfolio || '';

  return {
    ...normalized,
    portfolio_id: portfolioRecord?.id || '',
    sub_portfolio_id: subPortfolioRecord?.id || '',
    portfolio: nextPortfolio,
    sub_portfolio: nextSubPortfolio,
    group: nextPortfolio,
    site: nextSubPortfolio,
  };
}

export function attachCompanyLinks(asset = {}, companies = []) {
  const normalized = normalizeAsset(asset);
  const nextAsset = { ...normalized };

  companySections.forEach((section) => {
    const idField = `${section.key}_company_id`;
    const nameField = `${section.key}_name`;
    const contactPersonField = `${section.key}_contact_person`;
    const contactEmailField = `${section.key}_contact_email`;
    const contactPhoneField = `${section.key}_contact_phone`;

    let companyId = nextAsset[idField] || '';
    let company = companyId ? companies.find((record) => record.id === companyId) : null;

    if (!company && nextAsset[nameField]) {
      company = companies.find(
        (record) =>
          record.name === nextAsset[nameField] &&
          record.company_type === section.companyType &&
          (!nextAsset.org_id || !record.org_id || record.org_id === nextAsset.org_id)
      );
      companyId = company?.id || '';
    }

    nextAsset[idField] = companyId;
    if (company) {
      nextAsset[nameField] = company.name || '';
      nextAsset[contactPersonField] = company.contact_person || '';
      nextAsset[contactEmailField] = company.contact_email || '';
      nextAsset[contactPhoneField] = company.contact_phone || '';
    }
  });

  return nextAsset;
}

export function validateAssetForm(form) {
  const missing = [];
  if (!form.asset_id?.trim()) missing.push('Asset ID');
  if (!form.name?.trim()) missing.push('Asset name');
  if (!form.portfolio?.trim()) missing.push('Portfolio');
  if (!form.sub_portfolio?.trim()) missing.push('Sub-portfolio');
  if (!form.dc_installed_capacity_kwp?.toString().trim()) missing.push('DC installed capacity (kWp)');
  return missing;
}

function parseOptionalNumber(value) {
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function trimStrings(payload) {
  return Object.fromEntries(
    Object.entries(payload).map(([key, value]) => [
      key,
      typeof value === 'string' ? value.trim() : value,
    ])
  );
}

export function prepareAssetPayload(form, users = []) {
  const selectedManager = users.find((user) => user.id === form.asset_manager_id);
  const type = form.type || 'solar';
  const assetTypeOptions = getAssetTypeOptions(type);
  const assetTypeValues = assetTypeOptions.map((option) => option.value);
  const assetType = assetTypeValues.includes(form.asset_type)
    ? form.asset_type
    : getDefaultAssetType(type);

  const payload = trimStrings({
    ...form,
    type,
    asset_type: assetType,
    portfolio_id: form.portfolio_id || '',
    portfolio: form.portfolio,
    sub_portfolio_id: form.sub_portfolio_id || '',
    sub_portfolio: form.sub_portfolio,
    group: form.portfolio,
    site: form.sub_portfolio,
    lifecycle_status: form.lifecycle_status || 'planned_initiative',
    operational_status: form.operational_status || 'operational',
    status: form.operational_status || 'operational',
    dc_installed_capacity_kwp: parseOptionalNumber(form.dc_installed_capacity_kwp) || 0,
    export_capacity_kva: parseOptionalNumber(form.export_capacity_kva),
    import_capacity_kva: parseOptionalNumber(form.import_capacity_kva),
    asset_manager_name: selectedManager?.name || form.asset_manager_name || '',
  });

  if (form.latitude || form.longitude) {
    payload.location = {
      ...(form.latitude ? { lat: parseFloat(form.latitude) } : {}),
      ...(form.longitude ? { lon: parseFloat(form.longitude) } : {}),
    };
  } else {
    payload.location = {};
  }

  delete payload.latitude;
  delete payload.longitude;

  return payload;
}

export function handleTechnologyChange(form, technology) {
  const assetTypeValues = getAssetTypeOptions(technology).map((option) => option.value);
  return {
    ...form,
    type: technology,
    asset_type: assetTypeValues.includes(form.asset_type)
      ? form.asset_type
      : getDefaultAssetType(technology),
  };
}

export function handlePortfolioSelection(form, portfolioId, portfolios = [], subPortfolios = []) {
  const selectedPortfolio = portfolios.find((portfolio) => portfolio.id === portfolioId);
  const currentSubPortfolioValid = subPortfolios.some(
    (record) => record.id === form.sub_portfolio_id && record.portfolio_id === portfolioId
  );

  return {
    ...form,
    portfolio_id: portfolioId,
    portfolio: selectedPortfolio?.name || '',
    sub_portfolio_id: currentSubPortfolioValid ? form.sub_portfolio_id : '',
    sub_portfolio: currentSubPortfolioValid ? form.sub_portfolio : '',
  };
}

export function handleSubPortfolioSelection(form, subPortfolioId, subPortfolios = []) {
  const selectedSubPortfolio = subPortfolios.find((record) => record.id === subPortfolioId);
  return {
    ...form,
    sub_portfolio_id: subPortfolioId,
    sub_portfolio: selectedSubPortfolio?.name || '',
    portfolio_id: selectedSubPortfolio?.portfolio_id || form.portfolio_id,
    portfolio: selectedSubPortfolio?.portfolio_name || form.portfolio,
  };
}

export function handleCompanySelection(form, sectionKey, companyId, companies = []) {
  const selectedCompany = companies.find((record) => record.id === companyId);
  return {
    ...form,
    [`${sectionKey}_company_id`]: companyId,
    [`${sectionKey}_name`]: selectedCompany?.name || '',
    [`${sectionKey}_contact_person`]: selectedCompany?.contact_person || '',
    [`${sectionKey}_contact_email`]: selectedCompany?.contact_email || '',
    [`${sectionKey}_contact_phone`]: selectedCompany?.contact_phone || '',
  };
}
